import React from 'react';
import { useRef, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  PageHeader,
  StyledList,
  GoBackButton,
  ScrollableJobs,
  ImageTextOverlay
} from '../../components';
import { INTERNSHIP_CONTENT } from '../../data';
import { internData } from '../../data/jobs/internData';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import wallpaperImage03 from '../../assets/images/how-to-join/wallpaper-image-03.jpg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const InternshipPage = ({ setRefs }) => {
  const overviewRef = useRef(null);
  const expectationsRef = useRef(null);
  const whyJoinRef = useRef(null);
  const lookingForRef = useRef(null);
  const lookingForDescriptionRef = useRef(null);
  const applyRef = useRef(null);

  const refs = useMemo(
    () => ({
      overview: overviewRef,
      expectations: expectationsRef,
      whyJoin: whyJoinRef,
      lookingFor: lookingForRef,
      lookingForDescription: lookingForDescriptionRef,
      apply: applyRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  // State for intern count
  // eslint-disable-next-line no-unused-vars
  const [visibleInternCount, setVisibleInternCount] = useState(4);

  return (
    <>
      <SEO
        title={seoData.internship.title}
        content={seoData.internship.content}
        keywords={seoData.internship.keywords}
        canonical={seoData.internship.canonical}
        ogImage={seoData.internship.ogImage}
      />
      {/* Back Button */}}
      <GoBackButton
        backTo={ROUTES.HOW_TO_JOIN.ROOT}
        backText='Back to How to Join'
      />
      {/* Main "Internship" section */}
      <PageHeader
        id='hero'
        title={INTERNSHIP_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <ImageTextOverlay
        imageSrc={wallpaperImage03}
        text={INTERNSHIP_CONTENT.INTRO[0]}
        height={{ xs: '320px', sm: '300px', md: '270px', lg: '270px' }}
        boxSx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center'
        }}
        textSx={{
          fontSize: { xs: '1.07rem', sm: '1.19rem', md: '1.3rem' },
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE,
          px: { xs: 2, sm: 3, md: 5 },
          zIndex: 2,
          width: '100%',
          maxWidth: { xs: '100%', md: '80%' }
        }}
        // containerSx should NOT have height, minHeight, or &:before!
        containerSx={{
          position: 'relative',
          width: '100%'
        }}
      />
      <SectionContent
        id='overview'
        ref={refs.overview}
        initialSpacing={false}
        text={[
          {
            text: INTERNSHIP_CONTENT.INTRO[1],
            marginBottom: SECTION_SPACING,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* What to Expect as an Intern */}
      <SectionContent
        id='expectations'
        ref={refs.expectations}
        title={INTERNSHIP_CONTENT.EXPECTATIONS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INTERNSHIP_CONTENT.EXPECTATIONS.INTRO,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={INTERNSHIP_CONTENT.EXPECTATIONS.ITEMS} />
      </Box>
      {/* Why Join Our Internship Program */}
      <SectionContent
        id='whyJoin'
        ref={refs.whyJoin}
        title={INTERNSHIP_CONTENT.WHY_JOIN.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INTERNSHIP_CONTENT.WHY_JOIN.INTRO,
            marginBottom: ELEMENT_SPACING * 2,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList items={INTERNSHIP_CONTENT.WHY_JOIN.ITEMS} />
      </Box>
      {/* Who We're Looking For */}
      <SectionContent
        id='lookingFor'
        ref={refs.lookingFor}
        title={INTERNSHIP_CONTENT.WHO_WE_LOOK_FOR.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={INTERNSHIP_CONTENT.WHO_WE_LOOK_FOR.TEXT.map(text => ({
          text,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={INTERNSHIP_CONTENT.WHO_WE_LOOK_FOR.SKILLS}
        />
      </Box>
      <SectionContent
        id='lookingForDescription'
        ref={refs.lookingForDescription}
        initialSpacing={false}
        text={[
          {
            text: INTERNSHIP_CONTENT.WHO_WE_LOOK_FOR.CONCLUSION,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Apply for Our Internship Program */}
      <SectionContent
        id='apply'
        ref={refs.apply}
        title={INTERNSHIP_CONTENT.APPLY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INTERNSHIP_CONTENT.APPLY.TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Internship Listing Section */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <ScrollableJobs
          jobs={internData}
          setJobCount={setVisibleInternCount}
          jobType='Internships'
        />
      </Box>
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {INTERNSHIP_CONTENT.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default InternshipPage;
